import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';

import {
  diffHistoryUiDatesMillis,
  formatHistoryDateTimeLocal,
  formatHistoryUiDate,
  formatDateToSave,
  getDateToSave,
  getDateWithTimeZone,
  isHistoryUiDateBefore,
  parseHistoryUiDate,
  shiftHistoryUiDate,
  US_LONG_AM_PM_UI
} from '@/solaris-lens-common-lib/src/utils/dates';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);
dayjs.extend(customParseFormat);

export { formatHistoryDateTimeLocal, formatHistoryUiDate };

export const DATE_RANGE_DIRECTION_BACK = -1;
export const DATE_RANGE_DIRECTION_FORWARD = 1;
export const FALLBACK_RANGE_MILLIS = 60 * 60 * 1000;

const HISTORY_UI_WALL_CLOCK_FORMAT = 'MM/DD/YYYY hh:mm:ss A';
const HISTORY_TZ_ABBR_SUFFIX = /\s+[A-Z]{2,5}$/i;

/**
 * Resolves the IANA timezone used for history "now" (END defaults).
 * @param {string} [timezone] - Turbine IANA timezone from the endpoint.
 * @returns {string} IANA timezone string.
 */
const resolveHistoryTimezone = (timezone?: string): string =>
  timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Formats the current instant as a history UI date in the turbine timezone.
 * START must be derived from this END via subtraction, not timezone-converted alone.
 * @param {string} [timezone] - Turbine IANA timezone from the endpoint.
 * @returns {string} History UI date string (`MM/DD/YYYY hh:mm:ss A z`).
 */
export const formatNowHistoryUiDate = (timezone?: string): string => {
  const tz = resolveHistoryTimezone(timezone);
  try {
    return getDateWithTimeZone({ date: new Date(), timezone: tz }).format(
      US_LONG_AM_PM_UI
    );
  } catch {
    return getDateWithTimeZone({
      date: new Date(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).format(US_LONG_AM_PM_UI);
  }
};

/**
 * Converts a history UI wall-clock date to UTC using the turbine IANA timezone.
 * @param {string} date - History UI date string.
 * @param {string} [timezone] - Turbine IANA timezone from the endpoint.
 * @returns {string} UTC timestamp for the API.
 */
export const getHistoryDateToSave = (
  date: string,
  timezone?: string
): string => {
  if (!date) return date;
  if (!timezone) return getDateToSave(date);

  const wallClock = String(date).trim().replace(HISTORY_TZ_ABBR_SUFFIX, '');
  const parsed = dayjs.tz(wallClock, HISTORY_UI_WALL_CLOCK_FORMAT, timezone);
  if (parsed.isValid()) {
    return parsed.utc().format(formatDateToSave);
  }

  return getDateToSave(date);
};

/**
 * Normalizes the max chart range to a positive millisecond value.
 * @param {number} [maxRangeMillis] - Candidate range from interval config.
 * @returns {number} Positive range in milliseconds.
 */
export const normalizeRangeMillis = (maxRangeMillis?: number): number => {
  if (typeof maxRangeMillis === 'number' && Number.isFinite(maxRangeMillis)) {
    const absoluteRange = Math.abs(maxRangeMillis);
    if (absoluteRange > 0) return absoluteRange;
  }
  return FALLBACK_RANGE_MILLIS;
};

/**
 * Derives START from END by subtracting the allowed range (wall-clock).
 * @param {string} endDate - END history UI date.
 * @param {number} [maxRangeMillis] - Max range in milliseconds.
 * @returns {string} START history UI date.
 */
export const getStartDateFromEnd = (
  endDate: string,
  maxRangeMillis?: number
): string => {
  const rangeMillis = normalizeRangeMillis(maxRangeMillis);
  return shiftHistoryUiDate(endDate, -rangeMillis);
};

/**
 * Derives END from START by adding the allowed range, clamped to turbine "now".
 * @param {string} startDate - START history UI date.
 * @param {number} [maxRangeMillis] - Max range in milliseconds.
 * @param {string} [timezone] - Turbine IANA timezone for "now".
 * @returns {string} END history UI date.
 */
export const getEndDateFromStart = (
  startDate: string,
  maxRangeMillis?: number,
  timezone?: string
): string => {
  const rangeMillis = normalizeRangeMillis(maxRangeMillis);
  const candidate = shiftHistoryUiDate(startDate, rangeMillis);
  const nowFormatted = formatNowHistoryUiDate(timezone);
  const candidateParsed = parseHistoryUiDate(candidate);
  const nowParsed = parseHistoryUiDate(nowFormatted);

  if (
    candidateParsed.isValid() &&
    nowParsed.isValid() &&
    (candidateParsed.isBefore(nowParsed) || candidateParsed.isSame(nowParsed))
  ) {
    return candidate;
  }

  return nowFormatted;
};

/**
 * Returns whether START is strictly before END.
 * @param {string} startDate - START history UI date.
 * @param {string} endDate - END history UI date.
 * @returns {boolean} True when START is before END.
 */
export const isStartBeforeEnd = (startDate: string, endDate: string): boolean =>
  isHistoryUiDateBefore(startDate, endDate);

/**
 * Ensures START is before END by recomputing START from END when needed.
 * @param {string} startDate - START history UI date.
 * @param {string} endDate - END history UI date.
 * @param {number} [maxRangeMillis] - Max range in milliseconds.
 * @returns {{ startDate: string, endDate: string }} Valid UI date range.
 */
export const ensureStartBeforeEnd = (
  startDate: string,
  endDate: string,
  maxRangeMillis?: number
): { startDate: string; endDate: string } => {
  if (isStartBeforeEnd(startDate, endDate)) {
    return { startDate, endDate };
  }

  return {
    startDate: getStartDateFromEnd(endDate, maxRangeMillis),
    endDate
  };
};

/**
 * Builds API fetch dates from UI START/END using turbine timezone for UTC conversion.
 * @param {string} startDate - START history UI date.
 * @param {string} endDate - END history UI date.
 * @param {number} [maxRangeMillis] - Max range in milliseconds.
 * @param {string} [timezone] - Turbine IANA timezone from the endpoint.
 * @returns {{ startDate: string, endDate: string }} UTC timestamps for the API.
 */
export const getFetchDateRange = (
  startDate: string,
  endDate: string,
  maxRangeMillis?: number,
  timezone?: string
): { startDate: string; endDate: string } => {
  const validRange = ensureStartBeforeEnd(
    String(startDate),
    String(endDate),
    maxRangeMillis
  );

  return {
    startDate: getHistoryDateToSave(validRange.startDate, timezone),
    endDate: getHistoryDateToSave(validRange.endDate, timezone)
  };
};

/**
 * Builds the initial chart range: END = now in turbine TZ, START = END − range.
 * @param {number} [maxRangeMillis] - Max range in milliseconds.
 * @param {string} [timezone] - Turbine IANA timezone from the endpoint.
 * @returns {{ startDate: string, endDate: string }} Initial UI date range.
 */
export const getInitialDateRange = (
  maxRangeMillis?: number,
  timezone?: string
): { startDate: string; endDate: string } => {
  const endDate = formatNowHistoryUiDate(timezone);
  return {
    startDate: getStartDateFromEnd(endDate, maxRangeMillis),
    endDate
  };
};

/**
 * Whether the range can move forward without going past turbine "now".
 * @param {string} endDate - Current END history UI date.
 * @param {string} [timezone] - Turbine IANA timezone from the endpoint.
 * @returns {boolean} True when END is still before turbine "now".
 */
export const canNavigateDateRangeForward = (
  endDate: string,
  timezone?: string
): boolean => {
  const nowFormatted = formatNowHistoryUiDate(timezone);
  return isHistoryUiDateBefore(endDate, nowFormatted);
};

/**
 * Returns the duration between START and END, or a fallback range.
 * @param {string} startDate - START history UI date.
 * @param {string} endDate - END history UI date.
 * @param {number} [fallbackRangeMillis] - Fallback when duration is invalid.
 * @returns {number} Duration in milliseconds.
 */
export const getDateRangeDurationMillis = (
  startDate: string,
  endDate: string,
  fallbackRangeMillis?: number
): number => {
  const durationMillis = diffHistoryUiDatesMillis(startDate, endDate);

  if (durationMillis > 0) return durationMillis;
  return normalizeRangeMillis(fallbackRangeMillis);
};

/**
 * Shifts the visible range backward/forward; clamps forward to turbine "now".
 * @param {string} startDate - START history UI date.
 * @param {string} endDate - END history UI date.
 * @param {number} direction - Back (-1) or forward (1).
 * @param {number} [fallbackRangeMillis] - Fallback range length.
 * @param {string} [timezone] - Turbine IANA timezone from the endpoint.
 * @returns {{ startDate: string, endDate: string } | null} Next range or null.
 */
export const getShiftedDateRange = (
  startDate: string,
  endDate: string,
  direction: number,
  fallbackRangeMillis?: number,
  timezone?: string
): { startDate: string; endDate: string } | null => {
  if (
    direction === DATE_RANGE_DIRECTION_FORWARD &&
    !canNavigateDateRangeForward(endDate, timezone)
  ) {
    return null;
  }

  const rangeMillis = getDateRangeDurationMillis(
    startDate,
    endDate,
    fallbackRangeMillis
  );
  if (!rangeMillis) return null;

  const shiftMillis = rangeMillis * direction;
  const nextStart = shiftHistoryUiDate(startDate, shiftMillis);
  const nextEnd = shiftHistoryUiDate(endDate, shiftMillis);

  if (
    direction === DATE_RANGE_DIRECTION_FORWARD &&
    !canNavigateDateRangeForward(nextEnd, timezone)
  ) {
    const nowFormatted = formatNowHistoryUiDate(timezone);
    return {
      startDate: getStartDateFromEnd(nowFormatted, rangeMillis),
      endDate: nowFormatted
    };
  }

  return ensureStartBeforeEnd(nextStart, nextEnd, rangeMillis);
};
